import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Layout components
import { Header, Footer } from "@/components/layout";
import { ThemeSwitchIndicator } from "@/components/ThemeSwitchIndicator";

// Integrations
import ZohoSalesIQ from "@/components/ZohoSalesIQ";

// Context Providers
import { ThemeProvider } from "@/context/ThemeContext";
import { CMSAuthProvider, useCMSAuth } from "@/context/CMSAuthContext";
import { StudentAuthProvider } from "@/context/StudentAuthContext";
import { AssetProvider } from "@/context/AssetContext";

// Public Pages
import { 
  AboutPage, 
  ServicesPage, 
  ContactPage,
  DestinationPage
} from "@/pages";
import { HomePageRedesigned } from "@/pages/HomePageRedesigned";
import { DestinationsListPage } from "@/pages/DestinationsListPage";
import { SuccessStoriesPage } from "@/pages/SuccessStoriesPage";
import { EventsPage } from "@/pages/EventsPage";
import EventDetailPage from "@/pages/EventDetailPage";
import { BlogPage } from "@/pages/BlogPage";
import DisclaimerPage from "@/pages/DisclaimerPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import BlogDetailPage from "@/pages/BlogDetailPage";
import UniversityDetailPage from "@/pages/UniversityDetailPage";
import UniversitiesListPage from "@/pages/UniversitiesListPage";
import IBPathwayExplorerPage from "@/pages/ib/IBPathwayExplorerPage";
import IBPathwayManager from "@/pages/admin/IBPathwayManager";

// Admin CMS Pages
import { 
  AdminLogin, 
  AdminDashboard, 
  UniversitiesManager,
  PendingApprovals 
} from "@/pages/admin";
import PartnerManagement from "@/pages/admin/PartnerManagement";
import BlogManager from "@/pages/admin/BlogManager";
import EventsManager from "@/pages/admin/EventsManager";
import ServicesManager from "@/pages/admin/ServicesManager";
import ContactManager from "@/pages/admin/ContactManager";
import UniversityPagesManager from "@/pages/admin/UniversityPagesManager";
import LeadManagementPage from "@/pages/admin/LeadManagementPage";
import ApplicationsManager from "@/pages/admin/ApplicationsManager";
import CounselorDashboard from "@/pages/admin/CounselorDashboard";
import ContactCenterDashboard from "@/pages/admin/ContactCenterDashboard";
import StudentManagement from "@/pages/admin/StudentManagement";
import AssetsManager from "@/pages/admin/AssetsManager";
import DestinationBannerManager from "@/pages/admin/DestinationBannerManager";
import HomepageCopyEditor from "@/pages/admin/HomepageCopyEditor";
import SectionsManager from "@/pages/admin/SectionsManager";
import AdminStudentActivityPage from "@/pages/admin/AdminStudentActivityPage";
import AdminResourceManager from "@/pages/admin/AdminResourceManager";
import AdminLocationManager from "@/pages/admin/AdminLocationManager";
import LegalPagesManager from "@/pages/admin/LegalPagesManager";
import AdminProgramManager from "@/pages/admin/AdminProgramManager";
import AdminComparisonManager from "@/pages/admin/AdminComparisonManager";
import AdminDestinationCompare from "@/pages/admin/AdminDestinationCompare";
import UniversityApprovals from "@/pages/admin/UniversityApprovals";
import TeamManager from "@/pages/admin/TeamManager";
import QuestionBankManager from "@/pages/admin/QuestionBankManager";
import SiteSettingsManager from "@/pages/admin/SiteSettingsManager";
import UniversityEditPage from "@/pages/admin/UniversityEditPage";
import PageContentManager from "@/pages/admin/PageContentManager";
import ZohoSyncManager from "@/pages/admin/ZohoSyncManager";
import StudentActivityManager from "@/pages/admin/StudentActivityManager";
import DocumentationManager from "@/pages/admin/DocumentationManager";
import UserManagement from "@/pages/admin/UserManagement";
import EventRegistrations from "@/pages/admin/EventRegistrations";
import EnglishVerificationDashboard from "@/pages/admin/EnglishVerificationDashboard";

// Student Portal Pages
import StudentLoginPage from "@/pages/portal/StudentLoginPage";
import AuthCallback from "@/pages/portal/AuthCallback";
import StudentDashboard from "@/pages/portal/StudentDashboard";
import FastTrackApplicationPage from "@/pages/student/FastTrackApplicationPage";
import ApplicationTrackerPage from "@/pages/student/ApplicationTrackerPage";
import UniversityBrowserPage from "@/pages/student/UniversityBrowserPage";
import StudentDocumentsPage from "@/pages/student/StudentDocumentsPage";
import DigiLockerCallback from "@/pages/student/DigiLockerCallback";
import SOPGeneratorPage from "@/pages/student/SOPGeneratorPage";
import AIUniversityFinder from "@/pages/portal/AIUniversityFinder";
import SavedUniversitiesPage from "@/pages/portal/SavedUniversitiesPage";
import LoanEligibilityChecker from "@/pages/portal/LoanEligibilityChecker";
import SearchAuditLog from "@/pages/portal/SearchAuditLog";
import GPACalculatorPage from "@/pages/portal/GPACalculatorPage";
import ResourceLibraryPage from "@/pages/portal/ResourceLibraryPage";
import ResourceDetailPage from "@/pages/portal/ResourceDetailPage";
import SearchDetailPage from "@/pages/portal/SearchDetailPage";
import EnglishTestPage from "@/pages/portal/EnglishTestPage";
import TestSessionPage from "@/pages/portal/TestSessionPage";
import TestResultsDashboard from "@/pages/portal/TestResultsDashboard";
import EnglishTestVerificationPage from "@/pages/student/EnglishTestVerificationPage";

// Chat Widget
import ChatWidget from "@/components/chat/ChatWidget";

// Student Services Page
import StudentServicesPage from "@/pages/StudentServicesPage";

// Location Pages
import { LocationsPage, LocationDetailPage } from "@/pages/locations";

// Program Pages (SEO Landing Pages)
import { ProgramsListPage, ProgramDetailPage } from "@/pages/programs";

// Comparison Pages (SEO Landing Pages)
import { ComparisonsPage, ComparisonDetailPage } from "@/pages/compare";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Track pageview in Plausible Analytics
    if (typeof window.plausible === 'function') {
      window.plausible('pageview');
    }
  }, [pathname]);
  
  return null;
};

// Main layout wrapper
const Layout = ({ children }) => {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <ThemeSwitchIndicator />
    </>
  );
};

// Not Found page
const NotFound = () => (
  <div className="min-h-screen pt-20 flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-ux-blue mb-4">404</h1>
      <p className="text-xl text-ux-charcoal mb-6">Page not found</p>
      <a href="/" className="text-ux-blue hover:underline">Go back home</a>
    </div>
  </div>
);

// Protected Route for Admin CMS
const ProtectedAdminRoute = ({ children }) => {
  const { isAuthenticated, loading } = useCMSAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-ux-cyan border-t-transparent rounded-full" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AssetProvider>
          <CMSAuthProvider>
            <StudentAuthProvider>
              <BrowserRouter>
              <ScrollToTop />
              {/* Zoho SalesIQ Chat - Only loads for Student Portal */}
              <ZohoSalesIQ />
              <Routes>
                {/* Public Website Routes - with Layout */}
                <Route path="/" element={<Layout><HomePageRedesigned /></Layout>} />
                <Route path="/about" element={<Layout><AboutPage /></Layout>} />
                <Route path="/services" element={<Layout><ServicesPage /></Layout>} />
                <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
                <Route path="/events" element={<Layout><EventsPage /></Layout>} />
                <Route path="/events/:eventId" element={<Layout><EventDetailPage /></Layout>} />
                <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
                <Route path="/blog/:slug" element={<Layout><BlogDetailPage /></Layout>} />
                <Route path="/success-stories" element={<Layout><SuccessStoriesPage /></Layout>} />
                <Route path="/disclaimer" element={<Layout><DisclaimerPage /></Layout>} />
                <Route path="/privacy_policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
                <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
                <Route path="/terms" element={<Layout><TermsOfServicePage /></Layout>} />
                
                {/* Destination Routes */}
                <Route path="/destinations" element={<Layout><DestinationsListPage /></Layout>} />
                <Route path="/destinations/:country" element={<Layout><DestinationPage /></Layout>} />
                
                {/* Universities List Page */}
                <Route path="/universities" element={<UniversitiesListPage />} />

                {/* IB Pathway Explorer (built for IB students) — SEO-friendly nested routes */}
                <Route path="/ib-pathway-explorer" element={<IBPathwayExplorerPage />} />
                <Route path="/ib" element={<IBPathwayExplorerPage />} />
                <Route path="/ib/:faculty" element={<IBPathwayExplorerPage />} />
                <Route path="/ib/:faculty/:stream" element={<IBPathwayExplorerPage />} />
                <Route path="/ib/:faculty/:stream/:programmeSlug" element={<IBPathwayExplorerPage />} />

                {/* University Detail Page (SEO-optimized) */}
                <Route path="/universities/:slug" element={<Layout><UniversityDetailPage /></Layout>} />
                <Route path="/university/:slug" element={<Layout><UniversityDetailPage /></Layout>} />
                
                {/* Student Services Pages */}
                <Route path="/student-services" element={<StudentServicesPage />} />
                <Route path="/student-services/:serviceType" element={<StudentServicesPage />} />
                
                {/* Location Pages (SEO Landing Pages — Office/City) */}
                <Route path="/locations" element={<Layout><LocationsPage /></Layout>} />
                <Route path="/locations/:slug" element={<Layout><LocationDetailPage /></Layout>} />
                
                {/* Program Pages (SEO Landing Pages) */}
                <Route path="/programs" element={<Layout><ProgramsListPage /></Layout>} />
                <Route path="/programs/:slug" element={<Layout><ProgramDetailPage /></Layout>} />
                
                {/* Comparison Pages (SEO Landing Pages) */}
                <Route path="/compare" element={<Layout><ComparisonsPage /></Layout>} />
                <Route path="/compare/:slug" element={<Layout><ComparisonDetailPage /></Layout>} />
                
                {/* Student Portal Routes - No public layout */}
                <Route path="/uac-desk/login" element={<StudentLoginPage />} />
                <Route path="/uac-desk/register" element={<StudentLoginPage defaultMode="register" />} />
                <Route path="/uac-desk/auth-callback" element={<AuthCallback />} />
                <Route path="/uac-desk/dashboard" element={<StudentDashboard />} />
                <Route path="/uac-desk/fast-track" element={<FastTrackApplicationPage />} />
                <Route path="/uac-desk/applications" element={<ApplicationTrackerPage />} />
                <Route path="/uac-desk/browse-universities" element={<UniversityBrowserPage />} />
                <Route path="/uac-desk/documents" element={<StudentDocumentsPage />} />
                <Route path="/uac-desk/documents/digilocker-callback" element={<DigiLockerCallback />} />
                <Route path="/uac-desk/sop" element={<SOPGeneratorPage />} />
                <Route path="/uac-desk/ai-finder" element={<AIUniversityFinder />} />
                <Route path="/uac-desk/saved" element={<SavedUniversitiesPage />} />
                <Route path="/uac-desk/loan-eligibility" element={<LoanEligibilityChecker />} />
                <Route path="/uac-desk/gpa-calculator" element={<GPACalculatorPage />} />
                <Route path="/uac-desk/resources" element={<ResourceLibraryPage />} />
                <Route path="/uac-desk/resources/:resourceId" element={<ResourceDetailPage />} />
                <Route path="/uac-desk/search/:sessionId" element={<SearchDetailPage />} />
                <Route path="/uac-desk/search-history" element={<SearchAuditLog />} />
                <Route path="/uac-desk/english-test" element={<EnglishTestPage />} />
                <Route path="/uac-desk/english-test/:testType/:section" element={<TestSessionPage />} />
                <Route path="/uac-desk/english-test/results" element={<TestResultsDashboard />} />
                <Route path="/uac-desk/english-verification" element={<EnglishTestVerificationPage />} />
                <Route path="/uac-desk" element={<Navigate to="/uac-desk/dashboard" replace />} />
                
                {/* Admin CMS Routes - No public layout */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="/admin/dashboard" element={
                  <ProtectedAdminRoute><AdminDashboard /></ProtectedAdminRoute>
                } />
                <Route path="/admin/universities" element={
                  <ProtectedAdminRoute><UniversitiesManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/universities/new" element={
                  <ProtectedAdminRoute><UniversityEditPage /></ProtectedAdminRoute>
                } />
                <Route path="/admin/universities/:id/edit" element={
                  <ProtectedAdminRoute><UniversityEditPage /></ProtectedAdminRoute>
                } />
                <Route path="/admin/university-pages" element={
                  <ProtectedAdminRoute><UniversityPagesManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/partners" element={
                  <ProtectedAdminRoute><PartnerManagement /></ProtectedAdminRoute>
                } />
                <Route path="/admin/blog" element={
                  <ProtectedAdminRoute><BlogManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/events" element={
                  <ProtectedAdminRoute><EventsManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/events/:eventId/registrations" element={
                  <ProtectedAdminRoute><EventRegistrations /></ProtectedAdminRoute>
                } />
                <Route path="/admin/services" element={
                  <ProtectedAdminRoute><ServicesManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/contact" element={
                  <ProtectedAdminRoute><ContactManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/locations" element={
                  <ProtectedAdminRoute><AdminLocationManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/legal-pages" element={
                  <ProtectedAdminRoute><LegalPagesManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/pending" element={
                  <ProtectedAdminRoute><PendingApprovals /></ProtectedAdminRoute>
                } />
                <Route path="/admin/leads" element={
                  <ProtectedAdminRoute><LeadManagementPage /></ProtectedAdminRoute>
                } />
                <Route path="/admin/zoho-sync" element={
                  <ProtectedAdminRoute><ZohoSyncManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/applications" element={
                  <ProtectedAdminRoute><ApplicationsManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/chat" element={
                  <ProtectedAdminRoute><ContactCenterDashboard /></ProtectedAdminRoute>
                } />
                <Route path="/admin/chat-old" element={
                  <ProtectedAdminRoute><CounselorDashboard /></ProtectedAdminRoute>
                } />
                <Route path="/admin/students" element={
                  <ProtectedAdminRoute><StudentManagement /></ProtectedAdminRoute>
                } />
                <Route path="/admin/students/:userId/activity" element={
                  <ProtectedAdminRoute><AdminStudentActivityPage /></ProtectedAdminRoute>
                } />
                <Route path="/admin/resources" element={
                  <ProtectedAdminRoute><AdminResourceManager /></ProtectedAdminRoute>
                } />
                {/* Locations removed - redirects to Contact page */}
                <Route path="/admin/programs" element={
                  <ProtectedAdminRoute><AdminProgramManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/comparisons" element={
                  <ProtectedAdminRoute><AdminComparisonManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/destination-compare" element={
                  <ProtectedAdminRoute><AdminDestinationCompare /></ProtectedAdminRoute>
                } />
                <Route path="/admin/ib-pathway" element={
                  <ProtectedAdminRoute><IBPathwayManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/university-approvals" element={
                  <ProtectedAdminRoute><UniversityApprovals /></ProtectedAdminRoute>
                } />
                <Route path="/admin/assets" element={
                  <ProtectedAdminRoute><AssetsManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/destination-banners" element={
                  <ProtectedAdminRoute><DestinationBannerManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/homepage-copy" element={
                  <ProtectedAdminRoute><HomepageCopyEditor /></ProtectedAdminRoute>
                } />
                <Route path="/admin/sections" element={
                  <ProtectedAdminRoute><SectionsManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/team" element={
                  <ProtectedAdminRoute><TeamManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/question-bank" element={
                  <ProtectedAdminRoute><QuestionBankManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/site-settings" element={
                  <ProtectedAdminRoute><SiteSettingsManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/page-content" element={
                  <ProtectedAdminRoute><PageContentManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/student-activities" element={
                  <ProtectedAdminRoute><StudentActivityManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/documentation" element={
                  <ProtectedAdminRoute><DocumentationManager /></ProtectedAdminRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedAdminRoute><UserManagement /></ProtectedAdminRoute>
                } />
                <Route path="/admin/english-verification" element={
                  <ProtectedAdminRoute><EnglishVerificationDashboard /></ProtectedAdminRoute>
                } />
                
                {/* 404 */}
                <Route path="*" element={<Layout><NotFound /></Layout>} />
              </Routes>
              <Toaster position="top-right" />
              
              {/* Chat Widget - appears on all pages except admin */}
              <ChatWidgetWrapper />
            </BrowserRouter>
          </StudentAuthProvider>
        </CMSAuthProvider>
        </AssetProvider>
      </ThemeProvider>
    </div>
  );
}

// Wrapper to conditionally show chat widget (not on admin or student portal pages)
const ChatWidgetWrapper = () => {
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isStudentPortal = location.pathname.startsWith('/uac-desk');
  
  // Hide chat widget on admin pages and student portal (student portal has its own AI counselor)
  if (isAdminPage || isStudentPortal) return null;
  return <ChatWidget />;
};

export default App;
